import { useCallback } from "react";

import { useAuth } from "./auth-provider";

/**
 * Hook to create an authenticated fetch function
 *
 * Features:
 * - Cookie-based authentication (primary method via credentials: "include")
 * - Bearer token support for iframe mode (when token is available in session)
 * - Automatic auth header injection
 * - Compatible with both OIDC and iframe authentication modes
 *
 * @returns Authenticated fetch function with same signature as native fetch
 */
export function useAuthenticatedFetch() {
  const { session } = useAuth();

  const authenticatedFetch = useCallback(
    (...args: Parameters<typeof fetch>) => {
      const [url, init] = args;

      // Prepare headers
      const headers = new Headers(init?.headers);

      // Add bearer token if available (iframe mode)
      if (session?.token) {
        headers.set("Authorization", `Bearer ${session.token}`);
      }

      // Always include cookies for authentication (OIDC mode)
      return fetch(url, {
        ...init,
        credentials: "include", // Always send cookies
        headers,
      });
    },
    [session?.token]
  );

  return authenticatedFetch;
}
